import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api-error.js';
import type { ApiClient } from './api-client.js';

/**
 * ファイルアップロード（core.FileService の 3 ステップ）:
 *   ① POST /files/uploads → { fileId, uploadUrl }（署名付き PUT）
 *   ② PUT uploadUrl（Content-Type = mime）— サーバは bytes を受けない
 *   ③ POST /files/:id/complete { contentHash(SHA-256 hex), sizeBytes, scanResult: 'skipped' }
 *
 * ★ 画像は縮小しない（電帳法・05 条件 2）。選んだ File をそのまま送る。
 */
export interface UploadScope {
  type: string;
  id?: string | null;
}

export interface UploadedFile {
  fileId: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  contentHash: string;
}

export type UploadPhase = 'hashing' | 'uploading' | 'finalizing';

export interface UploadOptions {
  supersedesFileId?: string | null;
  onPhase?: (phase: UploadPhase) => void;
  signal?: AbortSignal;
}

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function uploadFile(api: ApiClient, file: File, scope: UploadScope, opts: UploadOptions = {}): Promise<UploadedFile> {
  const mime = file.type || 'application/octet-stream';
  const created = await api.post<{ fileId: string; uploadUrl: string; expiresAt: string }>('/files/uploads', {
    filename: file.name || 'upload',
    mime,
    sizeBytes: file.size,
    scopeType: scope.type,
    scopeId: scope.id ?? null,
    supersedesFileId: opts.supersedesFileId ?? null,
  });

  opts.onPhase?.('hashing');
  const bytes = await file.arrayBuffer();
  const hashPromise = sha256Hex(bytes);

  opts.onPhase?.('uploading');
  let res: Response;
  try {
    res = await fetch(created.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': mime }, signal: opts.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'UPLOAD_FAILED', '写真を送れませんでした。もう一度お試しください');
  }
  if (!res.ok) throw new ApiError(res.status, 'UPLOAD_FAILED', '写真を送れませんでした。もう一度お試しください');

  const contentHash = await hashPromise;
  opts.onPhase?.('finalizing');
  await api.post(`/files/${created.fileId}/complete`, { contentHash, sizeBytes: file.size, scanResult: 'skipped' });
  return { fileId: created.fileId, filename: file.name, mime, sizeBytes: file.size, contentHash };
}

// ---------------------------------------------------------------------------
// useUploader — 複数ファイルの状態管理（C-23 添付サムネイル: アップロード中 / 失敗（再試行））
// ---------------------------------------------------------------------------
export type UploadStatus = 'uploading' | 'done' | 'failed';

export interface UploadItem {
  key: string;
  file: File;
  previewUrl: string | null;
  status: UploadStatus;
  phase: UploadPhase | null;
  result: UploadedFile | null;
  error: string | null;
}

export interface Uploader {
  items: UploadItem[];
  add(files: FileList | File[]): Promise<UploadItem[]>;
  retry(key: string): Promise<void>;
  remove(key: string): void;
  reset(): void;
  readonly busy: boolean;
}

export function useUploader(api: ApiClient, scope: UploadScope): Uploader {
  const [items, setItems] = useState<UploadItem[]>([]);
  const seq = useRef(0);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const update = useCallback((key: string, patch: Partial<UploadItem>) => {
    setItems((cur) => cur.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  const run = useCallback(
    async (item: UploadItem): Promise<UploadItem> => {
      update(item.key, { status: 'uploading', error: null, phase: 'hashing' });
      try {
        const result = await uploadFile(api, item.file, scopeRef.current, { onPhase: (phase) => update(item.key, { phase }) });
        update(item.key, { status: 'done', result, phase: null });
        return { ...item, status: 'done', result, phase: null, error: null };
      } catch (e) {
        const message = e instanceof Error ? e.message : '写真を送れませんでした';
        update(item.key, { status: 'failed', error: message, phase: null });
        return { ...item, status: 'failed', error: message, phase: null };
      }
    },
    [api, update],
  );

  const add = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      const created: UploadItem[] = list.map((file) => ({
        key: `u${++seq.current}`,
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: 'uploading',
        phase: 'hashing',
        result: null,
        error: null,
      }));
      setItems((cur) => [...cur, ...created]);
      return Promise.all(created.map((it) => run(it)));
    },
    [run],
  );

  const retry = useCallback(
    async (key: string) => {
      const item = items.find((i) => i.key === key);
      if (item) await run(item);
    },
    [items, run],
  );

  const remove = useCallback((key: string) => {
    setItems((cur) => {
      const target = cur.find((i) => i.key === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return cur.filter((i) => i.key !== key);
    });
  }, []);

  const reset = useCallback(() => {
    setItems((cur) => {
      for (const i of cur) if (i.previewUrl) URL.revokeObjectURL(i.previewUrl);
      return [];
    });
  }, []);

  // アンマウント時にプレビュー URL を解放する
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(
    () => () => {
      for (const i of itemsRef.current) if (i.previewUrl) URL.revokeObjectURL(i.previewUrl);
    },
    [],
  );

  return { items, add, retry, remove, reset, busy: items.some((i) => i.status === 'uploading') };
}
